import { Row, Col, Card, Button, Input, Form, Badge, Tabs, Spin, Checkbox, Alert } from "antd";
import React, { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import { useLocation, useNavigate, useParams } from "react-router";
import apiPath from "../../constants/apiPath";
import FileUpload from "../../components/FileUpload";
import languages from "../../config/language";
import DescriptionEditor from '../../components/DescriptionEditor';
import CategorySelect from "../../components/select/CategorySelect";
import SubCategorySelect from "../../components/select/SubCategorySelect";
import SegmentSelect from "../../components/select/SegmentSelect";
import VehicleManufacturerSelect from "../../components/select/VehicleManufacturerSelect";
import VehicleMappingSection from "../../components/VehicleMappingSection";
import TextArea from "antd/es/input/TextArea";
import { CheckLineIcon } from "lucide-react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import BrandSelect from "../../components/select/BrandSelect";
import { buildProductListSearch } from "./productNav";
const { TabPane } = Tabs;

function AddEdit() {

    const sectionName = "Product";
    const routeName = "product";
    const { id } = useParams();
    const isEdit = Boolean(id);

    const location = useLocation();
    const isTemp = location.pathname.includes('/temp/edit/');
    const query = new URLSearchParams(location.search);

    const api = {
        addEdit: isTemp ? apiPath.editTempProduct : apiPath.addEditProduct,
        category: apiPath.commonCategory,
        edit: isTemp ? apiPath.viewTempProduct : apiPath.editProduct,
    }
    const activeTab = query.get("tab") || "all";
    const isWithoutVehicleTab = activeTab === "without-vehicle";
    const [form] = Form.useForm();
    const { request } = useRequest()
    const [loading, setLoading] = useState(false)
    const [fetching, setFetching] = useState(false);
    const navigate = useNavigate();
    const [image, setImage] = useState([]);
    const [validationStatus, setValidationStatus] = useState({});
    const [allVehicle, setAllVehicle] = useState(true);

    const [brandId, setBrandId] = useState(null);
    const [categoryId, setCategoryId] = useState(null);
    const [subCategoryId, setSubCategoryId] = useState(null);
    const [segmentId, setSegmentId] = useState(null);

    const [vehicleOption, setVehicleOption] = useState(null);
    const [selectedManufacturers, setSelectedManufacturers] = useState([]);
    const [vehicleModelMappings, setVehicleModelMappings] = useState([]);

    const handleFieldChange = (data, lang, type) => {
        const name = form.getFieldValue(`${lang}_name`);
        const description = form.getFieldValue(`${lang}_description`);
        const isDescriptionEmpty = !description || (description.trim() === '<p></p>' || description.trim() === "");
        setValidationStatus(prevStatus => ({
            ...prevStatus,
            [lang]: !!name && !isDescriptionEmpty
        }));
    }

    const fetchData = (id) => {
        setFetching(true);
        request({
            url: api.edit + "/" + id,
            method: 'GET',
            onSuccess: (response) => {
                const raw = response.data;

                // Both edit and viewTempProduct return populated objects; extract plain IDs + names
                const toId = (val) => val?._id || val;
                const toName = (val) => (val && typeof val === 'object' ? val.en_name : undefined);
                const data = {
                    ...raw,
                    brand_id:      toId(raw.brand_id),
                    category_id:   toId(raw.category_id),
                    sub_category_id: toId(raw.sub_category_id),
                    segment_id:    toId(raw.segment_id),
                    vehicle_manufacturer_ids: (raw.vehicle_manufacturer_ids || []).map(toId),
                    vehicle_model_mappings: (raw.vehicle_model_mappings || []).map(m => ({
                        ...m,
                        manufacturer_id: toId(m.manufacturer_id),
                        manufacturer_name: toName(m.manufacturer_id) || m.manufacturer_name,
                        vehicle_id: toId(m.vehicle_id),
                        vehicle_name: toName(m.vehicle_id) || m.vehicle_name,
                        specific_models: (m.specific_models || []).map(toId),
                    })),
                };

                form.setFieldsValue({
                    ...data,
                    all_vehicle: data?.all_vehicle !== undefined ? data.all_vehicle : false
                });

                if (data?.image) {
                    setImage([{ url: raw.image_url || data.image }]);
                }

                if (data?.brand_id) {
                    setBrandId(data.brand_id);
                }
                if (data?.category_id) {
                    setCategoryId(data.category_id);
                }
                if (data?.sub_category_id) {
                    setSubCategoryId(data.sub_category_id);
                }
                if (data?.segment_id) {
                    setSegmentId(data.segment_id);
                }

                const isAllVehicle = data?.all_vehicle !== undefined ? data.all_vehicle : false;
                setAllVehicle(isAllVehicle);

                if (data?.vehicle_mapping_option) {
                    setVehicleOption(data.vehicle_mapping_option);

                    if (data.vehicle_mapping_option === 'specific' && data?.vehicle_model_mappings) {
                        setVehicleModelMappings(data.vehicle_model_mappings);
                    } else if (data.vehicle_mapping_option === 'manufacturers' && data?.vehicle_manufacturer_ids) {
                        setSelectedManufacturers(data.vehicle_manufacturer_ids);
                    }
                } else {
                    setVehicleOption(null);
                }

                setFetching(false);
            },
            onError: (error) => {
                setFetching(false);
                ShowToast(error, Severty.ERROR)
            }
        })
    }

    useEffect(() => {
        const initialValidationStatus = {};

        languages.forEach(lang => {
            initialValidationStatus[lang.short] = !!form.getFieldValue(`${lang.short}_name`) && !!form.getFieldValue(`${lang.short}_description`);
        });

        setValidationStatus(initialValidationStatus);
    }, [])


    const handleFile = (data) => {
        setImage(data);
        form.setFieldValue('image', data);
    }

    const onCreate = (values) => {

        const formData = new FormData();

        formData.append("part_number", values.part_number || "");
        formData.append("price", values.price || "");
        formData.append("ref_no", values.ref_no || "");
        formData.append("brand_id", values.brand_id || "");
        formData.append("segment_id", values.segment_id || "");
        formData.append("category_id", values.category_id || "");
        formData.append("sub_category_id", values.sub_category_id || "");
        formData.append("specification", values.specification || "");
        formData.append("pack_size", values.pack_size || "");

        // Vehicle mapping
        if (isEdit || isTemp) {
            formData.append("all_vehicle", vehicleOption === 'all' ? true : false);
            formData.append("vehicle_mapping_option", vehicleOption || "");
            if (vehicleOption === 'manufacturers') {
                formData.append("vehicle_manufacturer_ids", JSON.stringify(selectedManufacturers));
                formData.append("vehicle_model_mappings", JSON.stringify([]));
            } else if (vehicleOption === 'specific') {
                formData.append("vehicle_model_mappings", JSON.stringify(vehicleModelMappings));
                formData.append("vehicle_manufacturer_ids", JSON.stringify([]));
            } else {
                formData.append("vehicle_manufacturer_ids", JSON.stringify([]));
                formData.append("vehicle_model_mappings", JSON.stringify([]));
            }
        } else if (isWithoutVehicleTab) {
            formData.append("all_vehicle", vehicleOption === 'all' ? true : false);
        } else {
            formData.append("all_vehicle", values.all_vehicle || false);
            if (!values.all_vehicle && values.vehicle_manufacturer_ids) {
                formData.append("vehicle_manufacturer_ids", JSON.stringify(
                    Array.isArray(values.vehicle_manufacturer_ids) ? values.vehicle_manufacturer_ids : [values.vehicle_manufacturer_ids]
                ));
            }
        }

        if (isEdit && isWithoutVehicleTab === false) {
            languages.forEach((lang) => {
                formData.append(`${lang.short}_name`, values[`${lang.short}_name`] || "");
                formData.append(`${lang.short}_description`, values[`${lang.short}_description`] || "");
            });

            if (image && image[0]?.originFileObj) {
                formData.append("image", image[0].originFileObj);
            }
        }

        if (isEdit === false) {
            languages.forEach((lang) => {
                formData.append(`${lang.short}_name`, values[`${lang.short}_name`] || "");
                formData.append(`${lang.short}_description`, values[`${lang.short}_description`] || "");
            });

            if (image && image[0]?.originFileObj) {
                formData.append("image", image[0].originFileObj);
            }
        }

        setLoading(true)
        request({
            url: (isEdit || isTemp) ? `${api.addEdit}/${id}` : api.addEdit,
            method: 'POST',
            data: formData,
            onSuccess: (data) => {
                setLoading(false)
                if (data.status) {
                    ShowToast(data.message, Severty.SUCCESS)
                    if (isTemp) {
                        // The saved record now lives in the Incomplete (QC) queue,
                        // so land there while keeping the rest of the list state.
                        navigate(`/${routeName}${buildProductListSearch(location.search, "incomplete")}`);
                    } else {
                        navigate(`/${routeName}${location.search}`);
                    }
                } else {
                    ShowToast(data.message, Severty.ERROR)
                }
            },
            onError: (error) => {
                ShowToast(error?.response?.data?.message || "Something went wrong, Please try again later", Severty.ERROR)
                setLoading(false)
            },
        })
    };

    useEffect(() => {
        if (isEdit) {
            fetchData(id);
        }
    }, [id]);

    const handleClose = () => {
        navigate(`/${routeName}${location.search}`);
    };

    const handleSaveVehicleMapping = () => {
        if (vehicleOption === 'manufacturers' && selectedManufacturers.length === 0) {
            ShowToast("Please select at least one manufacturer", Severty.ERROR);
            return;
        }

        if (vehicleOption === 'specific' && vehicleModelMappings.length === 0) {
            ShowToast("Please add at least one vehicle mapping", Severty.ERROR);
            return;
        }

        const formData = new FormData();

        formData.append("all_vehicle", vehicleOption === 'all' ? true : false);
        formData.append("vehicle_mapping_option", vehicleOption);

        if (vehicleOption === 'manufacturers') {
            formData.append("vehicle_manufacturer_ids", JSON.stringify(selectedManufacturers));
            formData.append("vehicle_model_mappings", JSON.stringify([]));
        } else if (vehicleOption === 'specific') {
            formData.append("vehicle_model_mappings", JSON.stringify(vehicleModelMappings));
            formData.append("vehicle_manufacturer_ids", JSON.stringify([]));
        } else {
            formData.append("vehicle_manufacturer_ids", JSON.stringify([]));
            formData.append("vehicle_model_mappings", JSON.stringify([]));
        }

        setLoading(true);
        request({
            url: `${api.addEdit}/${id}`,
            method: 'POST',
            data: formData,
            onSuccess: (data) => {
                setLoading(false);
                if (data.status) {
                    ShowToast(data.message, Severty.SUCCESS);
                    navigate(`/${routeName}?tab=incomplete`);
                } else {
                    ShowToast(data.message, Severty.ERROR);
                }
            },
            onError: (error) => {
                ShowToast(error?.response?.data?.message || "Something went wrong, Please try again later", Severty.ERROR);
                setLoading(false);
            },
        });
    };

    return (
        <Card
            variant={false}
            className="criclebox tablespace mb-6 shadow-sm"
            title={`${isEdit ? "Edit" : "Add New"} ${sectionName}`}
            extra={
                <Button
                    type="primary"
                    onClick={handleClose}
                    icon={<ArrowLeftOutlined />}
                    className="flex items-center gap-1"
                >
                    Back
                </Button>
            }
        >
            <Spin spinning={fetching} tip="Loading product details...">


                {isEdit && isWithoutVehicleTab ? (
                    <>
                        <Card className="p-4 border-l-4 border-l-blue-500">

                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Vehicle Mapping Options</h3>
                            <Alert
                                message="Select one of the options below to assign vehicles to this product according to current product segment"
                                type="info"
                                showIcon
                                className="mb-4"
                            />

                            <Form layout="vertical" className="vehicle-mapping-form p-4">
                                <Form.Item label="Select Vehicle Mapping Option" required>
                                    <VehicleMappingSection
                                        segmentId={segmentId}
                                        value={{ vehicleOption, selectedManufacturers, vehicleModelMappings }}
                                        onChange={({ vehicleOption: vo, selectedManufacturers: sm, vehicleModelMappings: vmm }) => {
                                            setVehicleOption(vo);
                                            setSelectedManufacturers(sm);
                                            setVehicleModelMappings(vmm);
                                        }}
                                    />
                                </Form.Item>

                                <Form.Item className="btn-row float-right mt-3">
                                    <Button type="primary" loading={loading} onClick={handleSaveVehicleMapping}>
                                        <CheckLineIcon size={15} /> Save Vehicle Mapping
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </>

                ) : isEdit ? (

                    <Form className="edit-page-wrap" form={form} onFinish={onCreate} autoComplete="off" layout="verticle" name="blog_form">

                        <Row gutter={[24, 0]}>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Part Number"
                                    name="part_number"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please enter the part number!",
                                        },
                                    ]}
                                >
                                    <Input
                                        autoComplete="off"
                                        placeholder="Enter Part Number"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Price (INR)"
                                    name="price"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please enter the price!",
                                        },
                                        {
                                            pattern: new RegExp(/^[\.0-9]*$/),
                                            message: "Only Numeric Characters Allowed",
                                        },
                                    ]}
                                >
                                    <Input
                                        maxLength={80}
                                        autoComplete="off"
                                        placeholder="Enter Price"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Ref. No"
                                    name="ref_no"
                                    rules={[
                                    ]}
                                >
                                    <Input
                                        maxLength={80}
                                        autoComplete="off"
                                        placeholder="Enter Ref No"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Brand"
                                    name="brand_id"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please select the brand!",
                                        },
                                    ]}
                                >
                                    <BrandSelect
                                        value={brandId}
                                        onChange={(val) => {
                                            setBrandId(val);
                                            setCategoryId(null);
                                            setSubCategoryId(null);
                                            setSegmentId(null);
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Category"
                                    name="category_id"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please select the category!",
                                        },
                                    ]}
                                >
                                    <CategorySelect
                                        brandId={brandId}
                                        disabled={!brandId}
                                        shouldFetch={!!brandId}
                                        value={categoryId}
                                        onChange={(val) => {
                                            setCategoryId(val);
                                            setSubCategoryId(null);
                                            form.setFieldValue("sub_category_id", undefined);
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Sub Category"
                                    name="sub_category_id"
                                >
                                    <SubCategorySelect
                                        categoryId={categoryId}
                                        disabled={!categoryId}
                                        shouldFetch={!!categoryId}
                                        value={subCategoryId}
                                        onChange={(val) => {
                                            setSubCategoryId(val);
                                            form.setFieldValue("sub_category_id", val);
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Segment"
                                    name="segment_id"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please select the segment!",
                                        },
                                    ]}
                                >
                                    <SegmentSelect
                                        brandId={brandId}
                                        disabled={!brandId}
                                        shouldFetch={!!brandId}
                                        value={segmentId}
                                        onChange={(val) => {
                                            setSegmentId(val);
                                            setSelectedManufacturers([]);
                                            setVehicleModelMappings([]);
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Pack Size"
                                    name="pack_size"
                                    rules={[
                                        {
                                            pattern: new RegExp(/^[\.0-9]*$/),
                                            message: "Only Numeric Characters Allowed",
                                        },
                                    ]}
                                >
                                    <Input
                                        maxLength={80}
                                        autoComplete="off"
                                        placeholder="Enter Pack Size"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <div className="p-4 border rounded-lg mb-4">
                                    <h4 className="font-semibold mb-3">Vehicle Mapping</h4>
                                    <VehicleMappingSection
                                        segmentId={segmentId}
                                        value={{ vehicleOption, selectedManufacturers, vehicleModelMappings }}
                                        onChange={({ vehicleOption: vo, selectedManufacturers: sm, vehicleModelMappings: vmm }) => {
                                            setVehicleOption(vo);
                                            setSelectedManufacturers(sm);
                                            setVehicleModelMappings(vmm);
                                        }}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Tabs defaultActiveKey="1">
                            {languages.map((lang, key) => {
                                const statusCheck = validationStatus[lang.short] ?? false;
                                return (

                                    <TabPane tab={<span>{lang.long} {validationStatus[lang.short] === false && <Badge dot color="red" />}</span>} key={lang.short}>

                                        <Row gutter={[24, 0]}>
                                            <Col span={24} xs={24} sm={12} lg={12}>

                                                <Form.Item label={`Name (${lang.long})`} name={`${lang.short}_name`}
                                                    rules={[
                                                        ...(lang.short === 'en' ? [
                                                            { required: true, message: `Please enter the name in ${lang.long}!` }
                                                        ] : []),
                                                        { max: 100, message: "Name should not contain more than 100 characters!" },
                                                        { min: 2, message: "Name should contain at least 2 characters!" },
                                                    ]}
                                                    initialValue={form.getFieldValue(`${lang.short}_name`)}
                                                    onChange={(data) => handleFieldChange(data, lang.short, 'title')}
                                                >

                                                    <Input
                                                        maxLength={80}
                                                        autoComplete="off"
                                                        placeholder={"Enter " + lang.name + " Name"}
                                                        onChange={() => handleFieldChange("", lang?.short, "name")}
                                                    />

                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Row gutter={[24, 0]}>
                                            <Col span={24} xs={24} sm={24} lg={24}>
                                                <Form.Item label={`Description (${lang.long})`} name={`${lang.short}_description`}
                                                    rules={[
                                                        ...(lang.short === 'en' ? [
                                                            { required: true, message: `Please enter the name in ${lang.long}!` }
                                                        ] : []),
                                                        { min: 10, message: "Description should contain at least 10 characters!" },
                                                    ]}
                                                    initialValue={form.getFieldValue(`${lang.short}_description`)}
                                                    onChange={(data) => handleFieldChange(data, lang.short, 'description')}
                                                >

                                                    <DescriptionEditor
                                                        onChange={() => handleFieldChange("", lang?.short, "description")}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                    </TabPane>
                                );
                            })}
                        </Tabs>

                        <Row gutter={[24, 0]}>
                            <Col span={24}>
                                <Form.Item
                                    label="Specification"
                                    name="specification"
                                >
                                    <TextArea
                                        placeholder="Enter Specification"
                                        rows={4}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[24, 0]} className="mb-3">
                            <Col span={24} xs={24} sm={24} lg={12} xxl={6}>
                                <Form.Item
                                    label="Product Image"
                                    name="image"
                                >
                                    <FileUpload handleFile={handleFile} fileList={image} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item className="btn-row float-right mt-3">
                            <Button type="primary" loading={loading} htmlType="submit">
                                <CheckLineIcon size={15} /> Save Changes
                            </Button>
                        </Form.Item>

                    </Form>

                ) : (

                    <Form className="edit-page-wrap" form={form} onFinish={onCreate} autoComplete="off" layout="verticle" name="blog_form">

                        <Row gutter={[24, 0]}>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Part Number"
                                    name="part_number"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please enter the part number!",
                                        },
                                    ]}
                                >
                                    <Input
                                        autoComplete="off"
                                        placeholder="Enter Part Number"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Price (INR)"
                                    name="price"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please enter the price!",
                                        },
                                        {
                                            pattern: new RegExp(/^[\.0-9]*$/),
                                            message: "Only Numeric Characters Allowed",
                                        },
                                    ]}
                                >
                                    <Input
                                        maxLength={80}
                                        autoComplete="off"
                                        placeholder="Enter Price"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={8}>
                                <Form.Item
                                    label="Ref. No"
                                    name="ref_no"
                                    rules={[
                                        {
                                            min: 2,
                                            message: "Please enter atleast 2 characters!",
                                        },
                                    ]}
                                >
                                    <Input
                                        autoComplete="off"
                                        placeholder="Enter Reference No."
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12} xs={12} sm={12} md={8}>
                                <Form.Item
                                    name="brand_id"
                                    label="Select Brand"
                                    className="brand-elt"
                                    rules={[{ required: true, message: 'Please select a brand' }]}
                                >
                                    <BrandSelect
                                        value={brandId}
                                        onChange={(val) => {
                                            setBrandId(val);
                                            setCategoryId(null);
                                            setSubCategoryId(null);
                                            setSegmentId(null);
                                            form.setFieldValue("brand_id", val);
                                            form.setFieldValue("category_id", undefined);
                                            form.setFieldValue("sub_category_id", undefined);
                                            form.setFieldValue("segment_id", undefined);
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12} xs={12} sm={12} md={8}>
                                <Form.Item className="segment-elt"
                                    name="segment_id"
                                    label="Select Segment"
                                    rules={[{ required: true, message: 'Missing Segment Selection' }]}
                                >
                                    <SegmentSelect
                                        brandId={brandId}
                                        disabled={!brandId}
                                        shouldFetch={!!brandId}
                                        value={segmentId}
                                        onChange={(val) => {
                                            setSegmentId(val);
                                            form.setFieldValue("segment_id", val);
                                            setSelectedManufacturers([]);
                                            setVehicleModelMappings([]);
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12} xs={12} sm={12} md={8}>
                                <Form.Item className="category-elt"
                                    name="category_id"
                                    label="Select Category"
                                    rules={[{ required: true, message: 'Missing Category Selection' }]}
                                >
                                    <CategorySelect
                                        brandId={brandId}
                                        disabled={!brandId}
                                        shouldFetch={!!brandId}
                                        value={categoryId}
                                        onChange={(val) => {
                                            setCategoryId(val);
                                            setSubCategoryId(null);
                                            form.setFieldValue("category_id", val);
                                            form.setFieldValue("sub_category_id", undefined);
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12} xs={12} sm={12} md={8}>
                                <Form.Item
                                    name="sub_category_id"
                                    label="Select Sub Category"
                                >
                                    <SubCategorySelect
                                        categoryId={categoryId}
                                        disabled={!categoryId}
                                        shouldFetch={!!categoryId}
                                        value={subCategoryId}
                                        onChange={(val) => {
                                            setSubCategoryId(val);
                                            form.setFieldValue("sub_category_id", val);
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item name="all_vehicle" valuePropName="checked" initialValue={true}>
                                    <Checkbox onChange={(e) => {
                                        setAllVehicle(e.target.checked);
                                        form.setFieldValue("all_vehicle", e.target.checked);
                                    }}>
                                        <span className="font-medium">Available for All Vehicles</span>
                                    </Checkbox>
                                </Form.Item>
                                {!allVehicle && (
                                    <Form.Item name="vehicle_manufacturer_ids" label="Select Vehicle Manufacturers"
                                        rules={[{ required: true, message: 'Please select at least one vehicle manufacturer!' }]}
                                    >
                                        <VehicleManufacturerSelect
                                            segmentId={segmentId}
                                            value={form.getFieldValue("vehicle_manufacturer_ids")}
                                            onChange={(val) => form.setFieldValue("vehicle_manufacturer_ids", val)}
                                            multiple={true}
                                        />
                                    </Form.Item>
                                )}
                            </Col>

                            <Col span={24} xs={24} sm={24} md={24}>

                                <Tabs defaultActiveKey={languages[0].short}>

                                    {languages.map(lang => (

                                        <TabPane tab={<span>{lang.long} {validationStatus[lang.short] === false && <Badge dot color="red" />}</span>} key={lang.short}>

                                            <Form.Item label={`Name (${lang.long})`} name={`${lang.short}_name`}
                                                rules={[
                                                    ...(lang.short === 'en' ? [
                                                        { required: true, message: `Please enter the name in ${lang.long}!` }
                                                    ] : []),
                                                    { max: 100, message: "Name should not contain more than 100 characters!" },
                                                    { min: 2, message: "Name should contain at least 2 characters!" },
                                                ]}
                                                initialValue={form.getFieldValue(`${lang.short}_name`)}
                                                onChange={(data) => handleFieldChange(data, lang.short, 'title')}
                                            >

                                                <Input autoComplete="off" placeholder={`Enter name in ${lang.long}`} />
                                            </Form.Item>

                                            <Form.Item label={`Description (${lang.long})`} name={`${lang.short}_description`}
                                                rules={[
                                                    { required: lang.short === 'en', message: `Please enter the description in ${lang.long}!` },
                                                ]}
                                                initialValue={form.getFieldValue(`${lang.short}_description`)}
                                            >
                                                <DescriptionEditor value={form.getFieldValue(`${lang.short}_description`)} placeholder={`Enter Description in ${lang.long}`} onChange={(data) => handleFieldChange(data, lang.short, 'description')} />

                                            </Form.Item>
                                        </TabPane>
                                    ))}
                                </Tabs>
                            </Col>

                            <Col span={24} xs={24} sm={12} lg={12} xxl={12}>
                                <Form.Item
                                    label="Specification (Max: 300)"
                                    name="specification"
                                    rules={[
                                        {
                                            min: 2,
                                            message: "Please enter atleast 2 characters!",
                                        },
                                    ]}
                                >
                                    <TextArea
                                        rows={4}
                                        maxLength={300}
                                        minLength={2}
                                        autoComplete="off"
                                        placeholder="Enter Specification"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24} xs={24} sm={24} md={10}>
                                <Form.Item
                                    label={
                                        <div>
                                            <span>Image</span>
                                            <span className="ml-1 text-gray-500">
                                                (Accept : png, jpg, jpeg, avif, webp, svg, gif)
                                            </span>
                                        </div>
                                    }
                                    valuePropName="value" name="image"
                                /* rules={[
                                    {
                                        required: true,
                                        message: 'Please upload a image!',
                                    },
                                ]} */
                                >
                                    <FileUpload value={image} filterType={["image"]} btnName={'Image'} onChange={(data) => handleFile(data)} />

                                </Form.Item>
                            </Col>

                        </Row>

                        <Form.Item className="btn-row float-right mt-3">
                            <Button type="primary" loading={loading} htmlType="submit">
                                <CheckLineIcon size={15} /> {isEdit ? "Update" : "Submit"}
                            </Button>
                        </Form.Item>
                    </Form>
                )}
            </Spin>
        </Card>
    )
}

export default AddEdit;